import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

import type { ImageSize } from './ImageElement';

interface ImageSizeMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSizeChange: (size: ImageSize) => void;
  onDelete: () => void;
  anchorElement: HTMLElement | null;
  currentSize?: ImageSize;
}

const SIZE_OPTIONS: { label: string; value: ImageSize; description: string }[] =
  [
    { label: 'S', value: 'small', description: 'Small (200px)' },
    { label: 'M', value: 'medium', description: 'Medium (400px)' },
    { label: 'L', value: 'large', description: 'Large (100%)' },
  ];

/**
 * Walk up the DOM looking for an ancestor that scrolls (overflow auto/scroll
 * on either axis with content actually overflowing). Used to find the email
 * editor's scrollable body so we can clip the image rect to its visible
 * region — without this, an image taller than the editor's max-height makes
 * `getBoundingClientRect()` return bounds that extend past the visible scroll
 * viewport, dropping the menu far below the on-screen image.
 */
function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node);
    const scrollsY =
      (cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight;
    const scrollsX =
      (cs.overflowX === 'auto' || cs.overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth;
    if (scrollsY || scrollsX) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export const ImageSizeMenu: React.FC<ImageSizeMenuProps> = ({
  isOpen,
  onClose,
  onSizeChange,
  onDelete,
  anchorElement,
  currentSize,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Position menu near the anchor element
  useEffect(() => {
    if (!isOpen || !anchorElement || !menuRef.current) {
      return;
    }

    const rect = anchorElement.getBoundingClientRect();
    const menu = menuRef.current;

    // If the anchor lives inside a scrolling container (e.g. the email
    // editor body with `max-height: 300px; overflow-y: auto`), clip the
    // anchor rect to that container's visible region before positioning.
    // Otherwise a tall image's full bounds push the menu off-screen.
    const scroller = getScrollableAncestor(anchorElement);
    let visibleTop = rect.top;
    let visibleBottom = rect.bottom;
    let visibleLeft = rect.left;
    let visibleRight = rect.right;
    if (scroller) {
      const sr = scroller.getBoundingClientRect();
      visibleTop = Math.max(visibleTop, sr.top);
      visibleBottom = Math.min(visibleBottom, sr.bottom);
      visibleLeft = Math.max(visibleLeft, sr.left);
      visibleRight = Math.min(visibleRight, sr.right);
    }
    const visibleCenterX = (visibleLeft + visibleRight) / 2;

    // Position below the image, centered on the visible portion
    const menuWidth = menu.offsetWidth;
    let left = visibleCenterX - menuWidth / 2;
    let top = visibleBottom + 8;

    // Keep within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 8) {
      left = 8;
    }
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
    }
    if (top + menu.offsetHeight > viewportHeight - 8) {
      top = visibleTop - menu.offsetHeight - 8;
    }

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }, [isOpen, anchorElement]);

  // Close on click outside, scroll, or resize
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (shouldIgnoreOutsideInteraction(e.target)) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Close on scroll or resize to prevent menu from appearing detached
    const handleScrollOrResize = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, onClose]);

  const handleSizeClick = useCallback(
    (size: ImageSize) => {
      onSizeChange(size);
      onClose();
    },
    [onSizeChange, onClose]
  );

  const handleDeleteClick = useCallback(() => {
    onDelete();
    onClose();
  }, [onDelete, onClose]);

  if (!isOpen) {
    return null;
  }

  // Normalize current size for comparison
  const normalizedCurrentSize =
    typeof currentSize === 'number' ? undefined : currentSize;

  // Use portal to render at document.body level to avoid positioning issues
  // when parent has CSS transforms (like sidebar slide animations)
  const menuContent = (
    <div
      ref={menuRef}
      className='fixed z-[9999] bg-white rounded-lg shadow-lg border border-neutral-200 p-2 min-w-[160px]'
      role='menu'
      aria-label='Image size options'
    >
      {/* Size presets */}
      <div className='flex items-center gap-1 mb-2 pb-2 border-b border-neutral-100'>
        {SIZE_OPTIONS.map(option => (
          <button
            key={option.value}
            type='button'
            onClick={() => handleSizeClick(option.value)}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
              normalizedCurrentSize === option.value
                ? 'bg-blue-100 text-blue-700'
                : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
            }`}
            title={option.description}
            role='menuitem'
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Delete button */}
      <button
        type='button'
        onClick={handleDeleteClick}
        className='w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors'
        role='menuitem'
      >
        <svg
          className='w-4 h-4'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
          />
        </svg>
        <span>Delete image</span>
      </button>
    </div>
  );

  // Render via portal to document.body to escape any parent transforms
  return createPortal(menuContent, document.body);
};

export default ImageSizeMenu;
